// client/src/components/CropModal.jsx
// Fit an uploaded image/PDF to the kiosk screen (ADR-019). The 11" station
// is mounted PORTRAIT, so the crop viewport is locked to 10:16 and the export
// is exactly 1200×1920 JPEG — the ONLY thing that ever leaves the browser
// (the original file, possibly a 20MB PDF, is never uploaded).
//
// Hand-rolled canvas pan+zoom rather than a crop library: one locked aspect,
// drag + zoom, nothing else. PDFs are rasterized client-side with pdfjs-dist
// (page 1) via a dynamic import so ~1MB of pdf.js stays out of the main chunk.

import { useEffect, useRef, useState } from 'react';
import Modal from './Modal.jsx';

// Kiosk panel export size — portrait 10:16. Keep in sync with
// SCREENSAVER_W/H in src/services/devices.js.
const EXPORT_W = 1200;
const EXPORT_H = 1920;
// On-screen viewport (CSS px) — same aspect, small enough to fit a laptop
// screen next to the modal chrome.
const VIEW_W = 280;
const VIEW_H = 448;

// Zoom is relative to "cover" (1 = the image exactly fills the frame). The
// slider runs in log space, t ∈ [-1, 1] with 0 (cover) at the CENTER, so the
// manager can go both ways: out to 0.25× (whole image, white letterbox) or
// in to 4×.
const ZOOM_RANGE = 4;
const tToZoom = (t) => Math.pow(ZOOM_RANGE, t);

/** Rasterize page 1 of a PDF into a canvas big enough to crop from. */
async function pdfToCanvas(file) {
  const pdfjs = await import('pdfjs-dist');
  const workerUrl = (await import('pdfjs-dist/build/pdf.worker.min.mjs?url')).default;
  pdfjs.GlobalWorkerOptions.workerSrc = workerUrl;
  const doc = await pdfjs.getDocument({ data: await file.arrayBuffer() }).promise;
  const page = await doc.getPage(1);
  const base = page.getViewport({ scale: 1 });
  // Render so the smaller dimension is ≥ ~2400px (sharp enough for 1920),
  // capped to bound memory on huge pages.
  const scale = Math.min(4000 / Math.max(base.width, base.height), Math.max(1, 2400 / Math.min(base.width, base.height)));
  const viewport = page.getViewport({ scale });
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(viewport.width);
  canvas.height = Math.round(viewport.height);
  await page.render({ canvasContext: canvas.getContext('2d'), viewport }).promise;
  return canvas;
}

function loadImage(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => resolve(img); // url revoked on modal close
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('bad_image')); };
    img.src = url;
  });
}

export default function CropModal({ file, onCancel, onCropped }) {
  const canvasRef = useRef(null);
  // Mutable crop state lives in a ref — pointer-move redraws shouldn't
  // re-render React. { source, sw, sh, zoom, ox, oy } where ox/oy are the
  // source's top-left in viewport CSS px and zoom=1 means "cover".
  const crop = useRef(null);
  const [status, setStatus] = useState('loading'); // 'loading' | 'ready' | 'error'
  const [saving, setSaving] = useState(false);
  const [zoomT, setZoomT] = useState(0); // slider position, 0 = cover (center)
  // Mirror of zoomT for the manually-attached wheel handler, which lives
  // outside React's render cycle and must see the current value.
  const zoomTRef = useRef(0);

  // Load the source (image or PDF page 1), then center it at cover-zoom.
  useEffect(() => {
    let alive = true;
    setStatus('loading');
    const load = file.type === 'application/pdf' ? pdfToCanvas(file) : loadImage(file);
    load
      .then((source) => {
        if (!alive) return;
        const sw = source.naturalWidth || source.width;
        const sh = source.naturalHeight || source.height;
        crop.current = { source, sw, sh, zoom: 1, ox: 0, oy: 0 };
        centerAtZoom(1);
        zoomTRef.current = 0;
        setZoomT(0);
        setStatus('ready'); // first draw happens in the effect below, after the canvas mounts
      })
      .catch(() => { if (alive) setStatus('error'); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file]);

  // The canvas only exists once status flips to 'ready' — drawing straight
  // after setStatus would hit a null ref and leave the viewport blank. The
  // wheel listener is attached manually with passive:false — React's
  // synthetic onWheel can end up passive, which makes preventDefault a no-op
  // and scrolls the page instead of zooming.
  useEffect(() => {
    if (status !== 'ready') return;
    draw();
    const canvas = canvasRef.current;
    if (!canvas) return;
    const onWheel = (e) => {
      if (!crop.current) return;
      e.preventDefault();
      const dir = e.deltaY < 0 ? 1 : -1;
      applyZoomT(Math.min(1, Math.max(-1, zoomTRef.current + dir * 0.06)));
    };
    canvas.addEventListener('wheel', onWheel, { passive: false });
    return () => canvas.removeEventListener('wheel', onWheel);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  /** Scale factor from source px to viewport CSS px at the current zoom. */
  function scaleOf(c) {
    return Math.max(VIEW_W / c.sw, VIEW_H / c.sh) * c.zoom;
  }
  // Per axis: an image larger than the frame pans within it (no white gap on
  // that axis); an image smaller than the frame is centered (white letterbox).
  function clampOffsets(c) {
    const s = scaleOf(c);
    const w = c.sw * s;
    const h = c.sh * s;
    c.ox = w >= VIEW_W ? Math.min(0, Math.max(VIEW_W - w, c.ox)) : (VIEW_W - w) / 2;
    c.oy = h >= VIEW_H ? Math.min(0, Math.max(VIEW_H - h, c.oy)) : (VIEW_H - h) / 2;
  }
  function centerAtZoom(zoom) {
    const c = crop.current;
    if (!c) return;
    // Keep the viewport-center point of the source fixed while zooming.
    const sOld = scaleOf(c);
    c.zoom = zoom;
    const sNew = scaleOf(c);
    const cx = (VIEW_W / 2 - c.ox) / sOld;
    const cy = (VIEW_H / 2 - c.oy) / sOld;
    c.ox = VIEW_W / 2 - cx * sNew;
    c.oy = VIEW_H / 2 - cy * sNew;
    clampOffsets(c);
  }
  function draw() {
    const c = crop.current;
    const canvas = canvasRef.current;
    if (!c || !canvas) return;
    const dpr = window.devicePixelRatio || 1;
    if (canvas.width !== VIEW_W * dpr) {
      canvas.width = VIEW_W * dpr;
      canvas.height = VIEW_H * dpr;
    }
    const ctx = canvas.getContext('2d');
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    // White ground — shows through PNG transparency AND fills the letterbox
    // when zoomed out below cover. Matches the export exactly.
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, VIEW_W, VIEW_H);
    const s = scaleOf(c);
    ctx.drawImage(c.source, c.ox, c.oy, c.sw * s, c.sh * s);
  }

  // Pointer drag → pan.
  const drag = useRef(null);
  function onPointerDown(e) {
    if (!crop.current) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    drag.current = { x: e.clientX, y: e.clientY };
  }
  function onPointerMove(e) {
    if (!drag.current || !crop.current) return;
    const c = crop.current;
    c.ox += e.clientX - drag.current.x;
    c.oy += e.clientY - drag.current.y;
    drag.current = { x: e.clientX, y: e.clientY };
    clampOffsets(c);
    draw();
  }
  function onPointerUp() { drag.current = null; }
  function applyZoomT(t) {
    zoomTRef.current = t;
    centerAtZoom(tToZoom(t));
    setZoomT(t);
    draw();
  }

  async function save() {
    const c = crop.current;
    if (!c) return;
    setSaving(true);
    try {
      // Reproduce the viewport verbatim at export scale: white ground, then
      // the image at the same (scaled) placement — so letterbox areas from a
      // zoomed-out image land as white in the JPEG too.
      const out = document.createElement('canvas');
      out.width = EXPORT_W;
      out.height = EXPORT_H;
      const ctx = out.getContext('2d');
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, EXPORT_W, EXPORT_H);
      const k = EXPORT_W / VIEW_W;
      const s = scaleOf(c);
      ctx.drawImage(c.source, c.ox * k, c.oy * k, c.sw * s * k, c.sh * s * k);
      const blob = await new Promise((resolve) => out.toBlob(resolve, 'image/jpeg', 0.85));
      if (!blob) throw new Error('to_blob_failed');
      await onCropped(blob);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal open onClose={onCancel} title="התאמת התמונה למסך העמדה" maxWidth="max-w-md">
      <div className="space-y-4">
        {status === 'loading' && (
          <p className="text-sm text-ink-500 text-center py-16">טוען את הקובץ...</p>
        )}
        {status === 'error' && (
          <p className="rounded-2xl bg-joy-50 border border-joy-200 text-joy-900 px-4 py-3 text-sm">
            לא ניתן לקרוא את הקובץ. ודאו שזו תמונה או PDF תקינים ונסו שוב.
          </p>
        )}
        {status === 'ready' && (
          <>
            <div className="flex justify-center">
              <canvas
                ref={canvasRef}
                style={{ width: VIEW_W, height: VIEW_H, touchAction: 'none', cursor: 'move' }}
                className="rounded-xl border-2 border-brand-500 select-none"
                onPointerDown={onPointerDown}
                onPointerMove={onPointerMove}
                onPointerUp={onPointerUp}
                onPointerCancel={onPointerUp}
              />
            </div>
            <div className="flex items-center gap-3">
              <span className="text-xs font-bold text-ink-600 shrink-0">הקטנה</span>
              <input
                type="range"
                min="-1"
                max="1"
                step="0.01"
                value={zoomT}
                onChange={(e) => applyZoomT(Number(e.target.value))}
                className="w-full accent-brand-600"
                dir="rtl"
                aria-label="זום"
              />
              <span className="text-xs font-bold text-ink-600 shrink-0">הגדלה</span>
            </div>
            <p className="hint text-center">
              גררו להזזת התמונה · גלגלת העכבר או הסליידר לזום, לשני הכיוונים.
              בהקטנה השוליים יתמלאו ברקע לבן. מה שרואים כאן הוא בדיוק מה שיוצג
              בעמדה.
            </p>
          </>
        )}
        <div className="flex justify-end gap-2 pt-1">
          <button type="button" className="btn-secondary" onClick={onCancel} disabled={saving}>
            ביטול
          </button>
          <button type="button" className="btn-primary" onClick={save} disabled={status !== 'ready' || saving}>
            {saving ? 'שומר...' : 'שמירה'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
